import * as React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import { useApi } from "../hooks/useApi";
import {
  DEFAULT_ORGANIZATION_USER_ROLE,
  ORGANIZATION_USER_ROLES,
  ORGANIZATION_USER_ROLE_LABELS,
  formatOrganizationUserRoleLabel,
} from "../constants/organizationUserRoles.js";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminAccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useApi();
  
  // Initialize tab from URL parameter, default to 0
  const getInitialTab = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 1) {
        return tabIndex;
      }
    }
    return 0;
  };
  
  const [tabValue, setTabValue] = React.useState(getInitialTab);
  const [account, setAccount] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  // Edit form state
  const [editAccountName, setEditAccountName] = React.useState("");
  const [editAccountBlocked, setEditAccountBlocked] = React.useState(false);
  const [updatingAccount, setUpdatingAccount] = React.useState(false);
  const [updateAccountError, setUpdateAccountError] = React.useState(null);
  
  // Organizations state
  const [userOrganizations, setUserOrganizations] = React.useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = React.useState(false);
  const [organizationsError, setOrganizationsError] = React.useState(null);
  const [newOrganizationRole, setNewOrganizationRole] = React.useState(DEFAULT_ORGANIZATION_USER_ROLE);
  const [addingOrganization, setAddingOrganization] = React.useState(false);
  
  // Add Organization Modal State
  const [addOrgModalOpen, setAddOrgModalOpen] = React.useState(false);
  const [allOrganizationsList, setAllOrganizationsList] = React.useState([]);
  const [loadingAllOrgs, setLoadingAllOrgs] = React.useState(false);
  const [orgModalSearchQuery, setOrgModalSearchQuery] = React.useState("");
  const [selectedOrgForAdd, setSelectedOrgForAdd] = React.useState(null);

  const fetchAccount = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const account = await api.get(`/admin/accounts/${id}`);
      
      setAccount(account);
      setEditAccountName(account.name || "");
      setEditAccountBlocked(account.blocked || false);
    } catch (err) {
      console.error("Error fetching account:", err);
      setError(err.message || "Failed to fetch account");
    } finally {
      setLoading(false);
    }
  }, [id, api]);

  const fetchOrganizations = React.useCallback(async () => {
    if (!id) return;
    
    setLoadingOrganizations(true);
    setOrganizationsError(null);
    try {
      const userOrgs = await api.get(`/admin/accounts/${id}/organizations`);
      setUserOrganizations(userOrgs || []);
    } catch (err) {
      console.error("Error fetching organizations:", err);
      setOrganizationsError(err.message || "Failed to fetch organizations");
    } finally {
      setLoadingOrganizations(false);
    }
  }, [id, api]);

  const fetchAllOrganizations = React.useCallback(async (searchQuery = "") => {
    setLoadingAllOrgs(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", "20");
      if (searchQuery && searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }
      
      const allOrgsData = await api.get(`/admin/organizations?${params.toString()}`);
      setAllOrganizationsList(allOrgsData.data || []);
    } catch (err) {
      console.error("Error fetching all organizations:", err);
    } finally {
      setLoadingAllOrgs(false);
    }
  }, [api]);

  const handleOpenAddOrgModal = async () => {
    setAddOrgModalOpen(true);
    setOrgModalSearchQuery("");
    setSelectedOrgForAdd(null);
    await fetchAllOrganizations("");
  };

  // Debounce search
  React.useEffect(() => {
    if (!addOrgModalOpen) return;
    
    const timeoutId = setTimeout(() => {
      fetchAllOrganizations(orgModalSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [orgModalSearchQuery, addOrgModalOpen, fetchAllOrganizations]);

  const handleCloseAddOrgModal = () => {
    setAddOrgModalOpen(false);
    setOrgModalSearchQuery("");
    setSelectedOrgForAdd(null);
  };

  const handleSelectOrganization = (org) => {
    setSelectedOrgForAdd(org);
  };

  const handleAddSelectedOrganization = async () => {
    if (!account || !selectedOrgForAdd) return;

    setAddingOrganization(true);
    setOrganizationsError(null);

    try {
      await api.post(`/admin/accounts/${account.id}/organizations`, {
        organization_id: selectedOrgForAdd.id,
        role: newOrganizationRole,
        is_active: true,
      });

      // Refresh organizations and close modal
      fetchOrganizations();
      handleCloseAddOrgModal();
      setNewOrganizationRole(DEFAULT_ORGANIZATION_USER_ROLE);
    } catch (err) {
      console.error("Error adding organization:", err);
      setOrganizationsError(err.message || "Failed to add organization");
    } finally {
      setAddingOrganization(false);
    }
  };

  React.useEffect(() => {
    fetchAccount();
  }, [id, fetchAccount]);

  React.useEffect(() => {
    if (tabValue === 1) {
      fetchOrganizations();
    }
  }, [tabValue, fetchOrganizations]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchParams({ tab: newValue.toString() });
  };

  const handleUpdateAccount = async () => {
    if (!account) return;

    setUpdatingAccount(true);
    setUpdateAccountError(null);

    try {
      const payload = {};
      
      // Only include fields that have changed
      if (editAccountName !== account.name) {
        payload.name = editAccountName.trim() || null;
      }
      if (editAccountBlocked !== account.blocked) {
        payload.blocked = editAccountBlocked;
      }

      // If nothing changed, just return
      if (Object.keys(payload).length === 0) {
        setUpdatingAccount(false);
        return;
      }

      await api.patch(`/admin/accounts/${account.id}`, payload);

      // Refresh account data
      fetchAccount();
      setUpdateAccountError(null);
    } catch (err) {
      console.error("Error updating Auth0 account:", err);
      setUpdateAccountError(err.message || "Failed to update Auth0 account");
    } finally {
      setUpdatingAccount(false);
    }
  };


  const handleRemoveOrganization = async (organizationId) => {
    if (!account) return;

    if (!window.confirm("Are you sure you want to remove this organization from the user?")) {
      return;
    }

    setLoadingOrganizations(true);
    setOrganizationsError(null);

    try {
      await api.delete(`/admin/accounts/${account.id}/organizations/${organizationId}`);

      // Refresh organizations
      fetchOrganizations();
    } catch (err) {
      console.error("Error removing organization:", err);
      setOrganizationsError(err.message || "Failed to remove organization");
    } finally {
      setLoadingOrganizations(false);
    }
  };

  if (loading) {
    return (
      <>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Navigation />
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              flexGrow: 1,
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
              <Skeleton height={40} width={300} />
              <Skeleton height={200} sx={{ mt: 2 }} />
            </Container>
            <Footer />
          </Box>
        </Box>
      </>
    );
  }

  if (error || !account) {
    return (
      <>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Navigation />
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              flexGrow: 1,
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
              <Alert severity="error">{error || "Account not found"}</Alert>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/dashboard/accounts")}
                sx={{ mt: 2 }}
              >
                Back to Accounts
              </Button>
            </Container>
            <Footer />
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard/accounts")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Auth0 Accounts
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {account.email || account.id}
                </Typography>
              </Breadcrumbs>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <IconButton onClick={() => navigate("/dashboard/accounts")}>
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                  Auth0 Account: {account.email || account.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage account details and organization memberships
                </Typography>
              </Box>
            </Box>

            <Card>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Details" />
                  <Tab label="Organizations" />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                {updateAccountError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {updateAccountError}
                  </Alert>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={account.email || ""}
                    disabled
                    helperText="Email cannot be changed"
                  />
                  <TextField
                    fullWidth
                    label="Name"
                    value={editAccountName}
                    onChange={(e) => {
                      setEditAccountName(e.target.value);
                      setUpdateAccountError(null);
                    }}
                    placeholder="Enter account name"
                    disabled={updatingAccount}
                    helperText="Display name for the account"
                  />
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editAccountBlocked ? "blocked" : "active"}
                      onChange={(e) => {
                        setEditAccountBlocked(e.target.value === "blocked");
                        setUpdateAccountError(null);
                      }}
                      label="Status"
                      disabled={updatingAccount}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="blocked">Blocked</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleUpdateAccount}
                      disabled={updatingAccount || (editAccountName === account.name && editAccountBlocked === account.blocked)}
                      startIcon={updatingAccount ? <CircularProgress size={16} /> : <EditIcon />}
                    >
                      {updatingAccount ? "Updating..." : "Update Account"}
                    </Button>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                {organizationsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {organizationsError}
                  </Alert>
                )}
                
                {/* User Organizations List */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Current Organizations ({userOrganizations.length})
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleOpenAddOrgModal}
                    disabled={addingOrganization || loadingOrganizations}
                    startIcon={<AddIcon />}
                  >
                    Find and Add Organizations
                  </Button>
                </Box>
                {loadingOrganizations ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress />
                  </Box>
                ) : userOrganizations.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No organizations assigned
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Organization</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Joined</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userOrganizations.map((userOrg) => (
                          <TableRow key={userOrg.id}>
                            <TableCell>
                              {userOrg.organization_name || userOrg.account_name || userOrg.id}
                            </TableCell>
                            <TableCell>
                              <Chip label={formatOrganizationUserRoleLabel(userOrg.role)} size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={userOrg.is_active ? "Active" : "Inactive"}
                                color={userOrg.is_active ? "success" : "default"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {userOrg.joined_at
                                ? new Date(userOrg.joined_at).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveOrganization(userOrg.id)}
                                disabled={loadingOrganizations}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </TabPanel>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>

      {/* Add Organization Modal */}
      <Dialog open={addOrgModalOpen} onClose={handleCloseAddOrgModal} maxWidth="md" fullWidth>
        <DialogTitle>Select Organization to Add</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search organizations by name..."
              value={orgModalSearchQuery}
              onChange={(e) => setOrgModalSearchQuery(e.target.value)}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={newOrganizationRole}
                onChange={(e) => setNewOrganizationRole(e.target.value)}
                label="Role"
                disabled={addingOrganization || loadingAllOrgs}
              >
                {ORGANIZATION_USER_ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {ORGANIZATION_USER_ROLE_LABELS[r]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {loadingAllOrgs ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Organization Name</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allOrganizationsList.map((org, index) => {
                    const isMember = userOrganizations.some((uo) => uo.id === org.id);
                    const isSelected = selectedOrgForAdd?.id === org.id;
                    const isEven = index % 2 === 0;
                    return (
                        <TableRow
                          key={org.id}
                          onClick={() => !isMember && handleSelectOrganization(org)}
                          sx={{
                            cursor: isMember ? "not-allowed" : "pointer",
                            opacity: isMember ? 0.6 : 1,
                            bgcolor: isSelected 
                              ? "action.selected" 
                              : isEven 
                                ? "grey.50" 
                                : "background.paper",
                            "&:hover": isMember ? {} : { bgcolor: "action.hover" },
                          }}
                        >
                          <TableCell>
                            <Typography fontWeight={isSelected ? "bold" : "normal"}>
                              {org.organization_name || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>{org.account_name || "N/A"}</TableCell>
                          <TableCell>{org.state || "N/A"}</TableCell>
                          <TableCell>
                            <Chip
                              label={org.is_active ? "Active" : "Inactive"}
                              color={org.is_active ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            {isMember ? (
                              <Chip label="Already Member" size="small" color="default" />
                            ) : isSelected ? (
                              <Chip label="Selected" size="small" color="primary" />
                            ) : (
                              <Button size="small" variant="outlined">
                                Select
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {allOrganizationsList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">
                          {orgModalSearchQuery ? "No organizations found matching your search" : "No organizations available"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {selectedOrgForAdd && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "primary.light", borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Selected Organization
              </Typography>
              <Typography>
                {selectedOrgForAdd.organization_name || selectedOrgForAdd.account_name || selectedOrgForAdd.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Role: {newOrganizationRole}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddOrgModal}>Cancel</Button>
          <Button
            onClick={handleAddSelectedOrganization}
            variant="contained"
            disabled={!selectedOrgForAdd || addingOrganization || loadingAllOrgs}
            startIcon={addingOrganization ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {addingOrganization ? "Adding..." : "Add Organization"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
